import React, { useState } from 'react'
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Box, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTheme } from '@mui/material/styles'
import { useI18n } from '@/contexts/I18nContext'
import type { SupportedLocale } from '@/contexts/I18nContext'
import { buildKiotHeaderIconButtonSx, banhangKiotLike } from '@/components/layout/banhangKiotTokens'

interface LanguageSwitcherProps {
  /** Nút trên header Kiot: luôn sáng, full chiều cao bar. */
  headerMode?: boolean
  /** Danh sách locale nhúng trong menu account (MenuItem). */
  menuMode?: boolean
  /** Gọi sau khi đổi ngôn ngữ (vd. đóng menu account). */
  onPicked?: () => void
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  headerMode = false,
  menuMode = false,
  onPicked,
}) => {
  const theme = useTheme()
  const { locale, setLocale, availableLocales } = useI18n()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)
  const currentLanguage = availableLocales.find((l) => l.code === locale) || availableLocales[0]

  const handleLanguageChange = (code: SupportedLocale) => {
    setLocale(code)
    setAnchorEl(null)
    onPicked?.()
  }

  if (menuMode) {
    return (
      <>
        {availableLocales.map((loc) => (
          <MenuItem
            key={loc.code}
            onClick={() => handleLanguageChange(loc.code as SupportedLocale)}
            selected={loc.code === locale}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              px: 1,
              mx: 0.5,
              minHeight: 36,
              borderRadius: `${banhangKiotLike.navItemRadiusPx}px`,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Typography variant="body2" sx={{ fontSize: '1.15rem', lineHeight: 1 }}>
                {loc.flag}
              </Typography>
            </ListItemIcon>
            <ListItemText
              primary={loc.nativeName}
              primaryTypographyProps={{
                fontSize: 13,
                fontWeight: loc.code === locale ? 700 : 500,
                lineHeight: 1.25,
              }}
            />
          </MenuItem>
        ))}
      </>
    )
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const triggerSx = headerMode
    ? {
        ...buildKiotHeaderIconButtonSx({
          activeBg: theme.palette.banhang.navItemActiveBg,
          activeBgHover: theme.palette.banhang.navItemActiveBgHover,
        }),
        px: 1,
        width: 'auto',
        minWidth: 44,
        height: '100%',
        minHeight: 0,
        alignSelf: 'stretch',
      }
    : {
        color: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.22)',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.14)',
        },
      }

  return (
    <Box
      sx={
        headerMode
          ? { display: 'flex', alignItems: 'stretch', alignSelf: 'stretch', height: '100%', minHeight: 0 }
          : undefined
      }
    >
      <IconButton onClick={handleClick} size="small" sx={triggerSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: headerMode ? 0.25 : 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: headerMode ? '1.05rem' : '1.2rem', lineHeight: 1 }}>
            {currentLanguage.flag}
          </Typography>
          {!headerMode ? (
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {currentLanguage.code.toUpperCase()}
            </Typography>
          ) : null}
          <ExpandMoreIcon sx={{ fontSize: headerMode ? 16 : '1rem', opacity: 0.9 }} />
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              mt: 0.5,
              borderRadius: `${banhangKiotLike.filterSidebarCardRadiusPx}px`,
              border: '1px solid rgba(0, 0, 0, 0.14)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            },
          },
        }}
      >
        {availableLocales.map((loc) => (
          <MenuItem
            key={loc.code}
            onClick={() => handleLanguageChange(loc.code as SupportedLocale)}
            selected={loc.code === locale}
          >
            <ListItemIcon sx={{ minWidth: '32px' }}>
              <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                {loc.flag}
              </Typography>
            </ListItemIcon>
            <ListItemText primary={loc.nativeName} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default LanguageSwitcher
